import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, AlertTriangle, Check, RotateCcw, Search, Clock, CheckCircle, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

function SkuPicker({ onSelect, disabled }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/ss-in-transit/orbit-skus?search=${encodeURIComponent(search)}`)
        .then(r => { setResults(r.data || []); setOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center border border-zinc-200 rounded-sm h-8 px-2 bg-white">
        <Search size={12} className="text-zinc-400 mr-1 shrink-0" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Type SKU..." className="text-xs w-full bg-transparent outline-none" disabled={disabled}
          onFocus={() => { if (results.length) setOpen(true); }} data-testid="sku-picker-input" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-0.5 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-40 overflow-y-auto">
          {results.map(s => (
            <button key={s.sku_id} className="w-full text-left px-2 py-1.5 text-xs hover:bg-zinc-100"
              onClick={() => { onSelect(s); setSearch(`${s.sku_id} — ${s.sku_name}`); setOpen(false); }}>
              <span className="font-mono text-zinc-500 mr-1">{s.sku_id}</span>{s.sku_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function POAutocomplete({ platform, value, onChange, disabled }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!platform || value.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      api.get(`/ss-in-transit/po-suggestions?platform=${encodeURIComponent(platform)}&search=${encodeURIComponent(value)}`)
        .then(r => { setSuggestions(r.data || []); setOpen(true); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [value, platform]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <Input type="text" value={value} onChange={e => onChange(e.target.value)}
        placeholder="Type PO# or add manually..." className="h-8 text-xs" disabled={disabled}
        onFocus={() => { if (suggestions.length) setOpen(true); }} data-testid="po-autocomplete" />
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-0.5 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-40 overflow-y-auto">
          {suggestions.map(s => (
            <button key={s.po_number} className="w-full text-left px-2 py-1.5 text-xs hover:bg-zinc-100"
              onClick={() => { onChange(s.po_number); setOpen(false); }}>
              <span className="font-mono font-semibold">{s.po_number}</span>
              <span className="text-zinc-400 ml-2">({s.total_ordered} ordered)</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_BADGE = {
  awaiting_confirmation: { icon: Clock, color: "bg-amber-100 text-amber-800", label: "Awaiting Confirmation" },
  confirmed: { icon: CheckCircle, color: "bg-green-100 text-green-700", label: "Confirmed" },
  reversed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Reversed" },
};

export default function SSInTransitPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState([]);
  const [ssOptions, setSsOptions] = useState([]);
  const [tab, setTab] = useState("record");

  // Form state
  const [platform, setPlatform] = useState("");
  const [ssId, setSsId] = useState("");
  const [ssName, setSsName] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [grnNumber, setGrnNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ sku_id: "", sku_name: "", item_id: "", quantity: 0 }]);
  const [submitting, setSubmitting] = useState(false);

  // Entries & review
  const [entries, setEntries] = useState([]);
  const [reviewItems, setReviewItems] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [reverseReason, setReverseReason] = useState("");
  const [reversingId, setReversingId] = useState(null);

  useEffect(() => {
    api.get("/ss-in-transit/platforms").then(r => {
      const list = r.data || [];
      setPlatforms(list);
      if (list.length && !platform) setPlatform(list[0].value.toLowerCase());
    }).catch(() => {});
    api.get("/ss-in-transit/ss-options").then(r => setSsOptions(r.data || [])).catch(() => {});
  }, []);

  const loadEntries = useCallback(() => {
    if (!ssId) return;
    setEntriesLoading(true);
    Promise.all([
      api.get(`/ss-in-transit/entries?ss_customer_id=${ssId}`),
      api.get("/ss-in-transit/review-needed"),
    ]).then(([e, r]) => {
      setEntries(e.data || []);
      setReviewItems(r.data || []);
    }).catch(() => {})
      .finally(() => setEntriesLoading(false));
  }, [ssId]);

  useEffect(() => { if (tab !== "record") loadEntries(); }, [tab, loadEntries]);

  const addLine = () => setLines(l => [...l, { sku_id: "", sku_name: "", item_id: "", quantity: 0 }]);
  const removeLine = (i) => setLines(l => l.filter((_, idx) => idx !== i));
  const updateLine = (i, field, val) => setLines(l => l.map((line, idx) => idx === i ? { ...line, [field]: val } : line));

  const handleSubmit = async () => {
    const validLines = lines.filter(l => l.sku_id && l.quantity > 0);
    if (!ssId || !platform || !poNumber || !validLines.length) {
      toast.error("SS, Platform, PO#, and at least one SKU line are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/ss-in-transit/submit", {
        ss_customer_id: ssId, ss_name: ssName, platform, po_number: poNumber,
        grn_number: grnNumber, notes, lines: validLines,
      });
      toast.success(`${data.created_count} in-transit line(s) recorded`);
      setPoNumber(""); setGrnNumber(""); setNotes("");
      setLines([{ sku_id: "", sku_name: "", item_id: "", quantity: 0 }]);
      setTab("entries");
      loadEntries();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSubmitting(false);
  };

  const handleReverse = async (entryId) => {
    if (!reverseReason.trim()) { toast.error("Reversal reason is required"); return; }
    try {
      await api.post("/ss-in-transit/reverse", { entry_id: entryId, reason: reverseReason });
      toast.success("In-transit entry reversed — stock returned");
      setReversingId(null);
      setReverseReason("");
      loadEntries();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleManualConfirm = async (entryId) => {
    try {
      await api.post("/ss-in-transit/manual-confirm", { entry_id: entryId });
      toast.success("In-transit entry manually confirmed");
      loadEntries();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleResolveReview = async (reviewId) => {
    try {
      await api.post("/ss-in-transit/resolve-review", { review_id: reviewId, resolution: "Manually resolved" });
      toast.success("Review item resolved");
      loadEntries();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="ss-in-transit-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/ss-inventory")}>
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">In-Transit Deliveries</h1>
          <p className="text-sm text-zinc-500">Record stock shipped to platforms. Auto-confirmed when Kavita's GRN report arrives.</p>
        </div>
      </div>

      {/* SS Selector */}
      <div className="flex items-center gap-4 mb-4" data-testid="ss-selector">
        <Label className="text-xs font-semibold text-zinc-600 shrink-0">Super Stockist:</Label>
        <Select value={ssId} onValueChange={v => { setSsId(v); const ss = ssOptions.find(s => s.id === v); setSsName(ss?.name || ""); }}>
          <SelectTrigger className="w-64 h-8 text-xs" data-testid="ss-dropdown">
            <SelectValue placeholder="Select SS..." />
          </SelectTrigger>
          <SelectContent>
            {ssOptions.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200 mb-4" data-testid="in-transit-tabs">
        {[
          { key: "record", label: "Record In-Transit" },
          { key: "entries", label: `Entries (${entries.length})` },
          { key: "review", label: `Needs Review (${reviewItems.length})` },
        ].map(t => (
          <button key={t.key}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${tab === t.key ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
            onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* RECORD TAB */}
      {tab === "record" && (
        <div className="border border-zinc-200 rounded-sm p-4 space-y-4" data-testid="record-form">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px] text-zinc-500">Sales Channel</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="h-8 text-xs" data-testid="platform-dropdown"><SelectValue /></SelectTrigger>
                <SelectContent>{platforms.map(p => <SelectItem key={p.value} value={p.value.toLowerCase()}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">PO Number</Label>
              <POAutocomplete platform={platform} value={poNumber} onChange={setPoNumber} disabled={submitting} />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">GRN # (optional)</Label>
              <Input type="text" value={grnNumber} onChange={e => setGrnNumber(e.target.value)}
                placeholder="GRN number if known..." className="h-8 text-xs" data-testid="grn-input" />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Notes</Label>
              <Input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Optional notes..." className="h-8 text-xs" data-testid="notes-input" />
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-zinc-700">SKU Lines</Label>
              <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={addLine} data-testid="add-line-btn">
                <Plus size={12} className="mr-1" /> Add Line
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((line, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-zinc-50 rounded-sm border border-zinc-100" data-testid={`line-${i}`}>
                  <div className="flex-1">
                    <SkuPicker onSelect={s => { updateLine(i, "sku_id", s.sku_id); updateLine(i, "sku_name", s.sku_name); }} disabled={submitting} />
                  </div>
                  <div className="w-24">
                    <Input type="number" min={1} value={line.quantity || ""} onChange={e => updateLine(i, "quantity", parseInt(e.target.value) || 0)}
                      placeholder="Qty" className="h-8 text-xs text-center" data-testid={`qty-${i}`} />
                  </div>
                  {lines.length > 1 && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => removeLine(i)}>
                      <Trash2 size={12} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button className="h-8 text-xs" disabled={submitting || !ssId || !poNumber || !lines.some(l => l.sku_id && l.quantity > 0)}
              onClick={handleSubmit} data-testid="submit-in-transit-btn">
              <Check size={14} className="mr-1" /> {submitting ? "Submitting..." : "Submit In-Transit"}
            </Button>
          </div>
        </div>
      )}

      {/* ENTRIES TAB */}
      {tab === "entries" && (
        <div data-testid="entries-list">
          {entriesLoading ? <p className="text-xs text-zinc-400 py-4">Loading...</p> : (
            <div className="border border-zinc-200 rounded-sm">
              <table className="data-table w-full">
                <thead><tr><th>Status</th><th>Platform</th><th>PO#</th><th>SKU</th><th>Qty</th><th>GRN#</th><th>Recorded By</th><th>Date</th><th></th></tr></thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs">No in-transit entries for this SS.</td></tr>
                  ) : entries.map(e => {
                    const badge = STATUS_BADGE[e.status] || STATUS_BADGE.awaiting_confirmation;
                    const Icon = badge.icon;
                    return (
                      <tr key={e._id} data-testid={`entry-${e._id}`}>
                        <td><span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${badge.color}`}><Icon size={10} />{badge.label}</span></td>
                        <td className="text-xs">{e.platform}</td>
                        <td className="text-xs font-mono">{e.po_number}</td>
                        <td className="text-xs">{e.sku_name} <span className="text-zinc-400 font-mono">({e.sku_id})</span></td>
                        <td className="text-xs font-mono text-right">{e.quantity}{e.grn_received_qty != null && e.grn_received_qty !== e.quantity ? <span className="text-amber-600 ml-1">(GRN: {e.grn_received_qty})</span> : ""}</td>
                        <td className="text-xs font-mono text-zinc-400">{e.grn_number || e.grn_id || "—"}</td>
                        <td className="text-xs text-zinc-500">{e.recorded_by_name}</td>
                        <td className="text-xs text-zinc-400 font-mono">{(e.recorded_at || "").slice(0, 10)}</td>
                        <td>
                          {e.status === "awaiting_confirmation" && (
                            reversingId === e._id ? (
                              <div className="flex items-center gap-1">
                                <Input type="text" value={reverseReason} onChange={ev => setReverseReason(ev.target.value)}
                                  placeholder="Reason..." className="h-6 text-[10px] w-32" data-testid={`reverse-reason-${e._id}`} />
                                <Button size="sm" className="h-6 text-[10px] bg-red-600 hover:bg-red-700" onClick={() => handleReverse(e._id)} data-testid={`confirm-reverse-${e._id}`}>
                                  Confirm
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => { setReversingId(null); setReverseReason(""); }}>Cancel</Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                {e.platform !== "blinkit" && (
                                  <Button size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
                                    onClick={() => handleManualConfirm(e._id)} data-testid={`manual-confirm-btn-${e._id}`}>
                                    <CheckCircle size={10} className="mr-1" /> Confirm
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-500"
                                  onClick={() => setReversingId(e._id)} data-testid={`reverse-btn-${e._id}`}>
                                  <RotateCcw size={10} className="mr-1" /> Reverse
                                </Button>
                              </div>
                            )
                          )}
                          {e.status === "confirmed" && e.manually_confirmed && <span className="text-[9px] text-emerald-600 font-medium">Manual</span>}
                          {e.status === "reversed" && <span className="text-[9px] text-zinc-400">{e.reversal_reason}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* REVIEW TAB */}
      {tab === "review" && (
        <div data-testid="review-list">
          {reviewItems.length === 0 ? (
            <div className="text-center py-12 text-zinc-400 text-xs border border-dashed border-zinc-200 rounded-sm">
              No near-match items need review.
            </div>
          ) : (
            <div className="border border-amber-200 rounded-sm bg-amber-50/50">
              <div className="px-4 py-2 border-b border-amber-200 flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-600" />
                <h2 className="text-sm font-semibold text-amber-800">Near-Match Review — {reviewItems.length} items</h2>
              </div>
              <div className="divide-y divide-amber-100">
                {reviewItems.map(r => (
                  <div key={r._id} className="px-4 py-3 flex items-center gap-4" data-testid={`review-${r._id}`}>
                    <div className="flex-1">
                      <p className="text-xs"><strong>GRN PO#:</strong> <span className="font-mono">{r.po_number_grn}</span></p>
                      <p className="text-xs"><strong>Manual PO#:</strong> <span className="font-mono">{r.po_number_manual}</span></p>
                      <p className="text-xs text-zinc-500">SKU: {r.sku_name || r.sku_id} · GRN qty: {r.grn_qty} · In-transit qty: {r.in_transit_qty}</p>
                    </div>
                    <Button size="sm" className="h-7 text-[10px]" onClick={() => handleResolveReview(r._id)} data-testid={`resolve-${r._id}`}>
                      Mark Resolved
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
